import React from "react";
import {
  Card,
  CardContent,
  CardActions,
  Button,
  Typography,
  Avatar,
  AvatarGroup,
  Box,
  Chip,
  IconButton,
  Divider,
} from "@mui/material";
import {
  BookmarkBorder,
  Bookmark,
  LocationOn,
  Work,
  AccessTime,
  ShareOutlined,
} from "@mui/icons-material";
import Avatar_ from "./Avatar_";
import { ShareModal } from "./ShareModal";

const JobCard = ({
  title,
  company,
  pay,
  role,
  speciality,
  jobType,
  applicants,
  onApplyClick,
  onViewClick,
  recommended,
  jobId,
  job,
}) => {
  const [open, setOpen] = React.useState(false);
  const [id, setId] = React.useState(null);
  return (
    <Card
      sx={{
        borderRadius: "16px",
        boxShadow: "none",
        border: "1px solid #E5E7EB",
        minHeight: { xs: "300px", md: "280px" },
        transition: "all 0.2s ease-in-out",
        cursor: "pointer",
        backgroundColor: "#FAFAFA",
        "&:hover": {
          boxShadow: "0px 4px 12px 0px rgba(0,0,0,0.08)",
          transform: "translateY(-1px)",
        },
      }}
    >
      <CardContent
        sx={{ p: { xs: "20px 20px 0 20px", md: "32px 32px 0 32px" } }}
      >
        {/* Header with role chip and share icon */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            mb: { xs: 1.5, md: 0 },
          }}
        >
          <Chip
            label={role || speciality || "Medical Doctor (MD)"}
            size="small"
            sx={{
              backgroundColor: "rgba(41, 155, 141, 0.1)",
              color: "rgba(33, 64, 75, 1)",
              fontWeight: 500,
              fontSize: { xs: "0.7rem", md: "0.75rem" },
              height: { xs: "32px", md: "41px" },
              borderRadius: "999px",
              border: "1px solid #E0F2FE",
              "& .MuiChip-label": {
                px: { xs: 1, md: 1.5 },
              },
            }}
          />
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setId(jobId);
              setOpen(true);
            }}
            sx={{
              p: 0.5,
              color: "#9CA3AF",
              transition: "color 0.2s",
              height: { xs: "40px", md: "50px" },
              width: { xs: "40px", md: "50px" },
              bgcolor: "rgba(41, 155, 141, 0.1)",
              "&:hover": {
                color: "#21404B",
                backgroundColor: "rgba(41, 155, 141, 0.1)",
              },
            }}
          >
            <ShareOutlined
              sx={{ fontSize: { xs: "1rem", md: "1.1rem" }, color: "#21404B" }}
            />
          </IconButton>
        </Box>
        <ShareModal
          open={open}
          handleClose={() => setOpen(false)}
          url={`${window.location.href}job-details/${id}`}
        />

        {/* Job title */}
        <Typography
          variant="h6"
          component="h2"
          sx={{
            fontWeight: 600,
            fontSize: { xs: "1rem", md: "1.1rem" },
            lineHeight: 1.4,
            color: "#111827",
            mb: { xs: "12px", md: "16px" },
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            fontFamily: "'Montserrat', sans-serif",
            textTransform: "capitalize",
          }}
        >
          {title}dd
        </Typography>

        {/* Applicants section */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1, md: 1.5 },
            mb: { xs: 1.5, md: 2 },
          }}
        >
          <AvatarGroup
            max={4}
            sx={{
              "& .MuiAvatar-root": {
                width: { xs: 20, md: 24 },
                height: { xs: 20, md: 24 },
                fontSize: "0.7rem",
                border: "2px solid white",
                marginLeft: { xs: "-6px", md: "-8px" },
              },
              "& .MuiAvatarGroup-avatar": {
                backgroundColor: "#E5E7EB",
                color: "#6B7280",
                fontSize: { xs: "0.6rem", md: "0.65rem" },
                fontWeight: 500,
              },
            }}
          >
            {applicants?.length > 0 ? (
              applicants?.slice(0, 3)?.map((applicant, index) => (
                <Avatar_
                  name={applicant?.name}
                  photo={applicant.photo}
                  key={index}
                  sx={{
                    width: { xs: 20, md: 24 },
                    height: { xs: 20, md: 24 },
                  }}
                />
              ))
            ) : (
              <Avatar_
                name={"John"}
                sx={{ width: { xs: 20, md: 24 }, height: { xs: 20, md: 24 } }}
              />
            )}
          </AvatarGroup>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 400,
              fontSize: { xs: "0.8rem", md: "0.875rem" },
              color: "rgba(0, 0, 0, 1)",
            }}
          >
            {applicants?.length > 0
              ? `${applicants.length}+ Applicant${
                  applicants.length !== 1 ? "s" : ""
                }`
              : "48+ Applicants"}
          </Typography>
        </Box>

        {/* Job details with icons */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: { xs: 1, md: 1.5 },
            mb: { xs: 2, md: 3 },
            bgcolor: "#EAF5F466",
            borderRadius: "10px",
            p: { xs: "8px 12px", md: "11px 16px" },
            border: "0.5px solid #E7E4EC",
          }}
        >
          {/* First row with location and job type */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 2, md: 3 },
              justifyContent: "center",
              borderBottom: "1px solid #D9D4E0",
              pb: { xs: "12px", md: "16px" },
              flexDirection: { xs: "column", sm: "row" },
              textAlign: { xs: "center", sm: "left" },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <svg
                width="20"
                height="21"
                viewBox="0 0 24 25"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15.5 11.5C15.5 13.433 13.933 15 12 15C10.067 15 8.5 13.433 8.5 11.5C8.5 9.567 10.067 8 12 8C13.933 8 15.5 9.567 15.5 11.5Z"
                  stroke="black"
                />
                <path
                  d="M12 2.5C16.8706 2.5 21 6.53298 21 11.4258C21 16.3965 16.8033 19.8847 12.927 22.2567C12.6445 22.4162 12.325 22.5 12 22.5C11.675 22.5 11.3555 22.4162 11.073 22.2567C7.2039 19.8616 3 16.4137 3 11.4258C3 6.53298 7.12944 2.5 12 2.5Z"
                  stroke="black"
                />
              </svg>
              <Typography
                variant="body2"
                sx={{
                  color: "#1F2937",
                  fontSize: "14px",
                  fontWeight: 500,
                  fontFamily: "'Montserrat', sans-serif",
                  lineHeight: "24px",
                }}
              >
                {job?.client_city?.length > 15
                  ? `${job?.client_city?.slice(0, 15)}...`
                  : job?.client_city || "--"}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                borderLeft: { xs: "none", sm: "1px solid #D9D4E0" },
                pl: { xs: 0, sm: "16px" },
                borderTop: { xs: "1px solid #D9D4E0", sm: "none" },
                pt: { xs: "12px", sm: 0 },
              }}
            >
              <svg
                width="20"
                height="21"
                viewBox="0 0 24 25"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11.0065 22H9.60546C6.02021 22 4.22759 22 3.11379 20.865C2 19.7301 2 17.9034 2 14.25C2 10.5966 2 8.76992 3.11379 7.63496C4.22759 6.5 6.02021 6.5 9.60546 6.5H13.4082C16.9934 6.5 18.7861 6.5 19.8999 7.63496C20.7568 8.50819 20.9544 9.7909 21 12V13.5"
                  stroke="black"
                  strokeLinecap="round"
                />
                <path
                  d="M19 19H16M16 22C14.3431 22 13 20.6569 13 19C13 17.3431 14.3431 16 16 16M19 22C20.6569 22 22 20.6569 22 19C22 17.3431 20.6569 16 19 16"
                  stroke="black"
                  strokeLinecap="round"
                />
                <path
                  d="M16 6.5L15.9006 6.19094C15.4056 4.65089 15.1581 3.88087 14.5688 3.44043C13.9796 3 13.1969 3 11.6315 3H11.3684C9.80304 3 9.02035 3 8.43111 3.44043C7.84186 3.88087 7.59435 4.65089 7.09934 6.19094L7 6.5"
                  stroke="black"
                />
              </svg>
              <Typography
                variant="body2"
                sx={{
                  color: "#1F2937",
                  fontSize: "14px",
                  fontWeight: 500,
                  fontFamily: "'Montserrat', sans-serif",
                  lineHeight: "24px",
                }}
              >
                {jobType || "Locum"}
              </Typography>
            </Box>
          </Box>

          {/* Second row with open positions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              justifyContent: "center",
            }}
          >
            <svg
              width="20"
              height="21"
              viewBox="0 0 24 25"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 8H13.5C14.3284 8 15 8.67157 15 9.5M12 8H10.5C9.67157 8 9 8.67157 9 9.5V10C9 10.8284 9.67157 11.5 10.5 11.5H13.5C14.3284 11.5 15 12.1716 15 13V13.5C15 14.3284 14.3284 15 13.5 15H12M12 8V6.5M12 15H10.5C9.67157 15 9 14.3284 9 13.5M12 15V16.5"
                stroke="black"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M10.94 22.0124L9.02913 20.8073C8.54415 20.5014 8.30166 20.3485 8.03253 20.3397C7.74172 20.3301 7.49493 20.4768 6.97087 20.8073C6.38395 21.1774 5.21687 22.1971 4.46195 21.7108C4 21.4133 4 20.6575 4 19.1458V8.50002C4 5.67158 4 4.25736 4.82699 3.37868C5.65399 2.5 6.98501 2.5 9.64706 2.5H14.3529C17.015 2.5 18.346 2.5 19.173 3.37868C20 4.25736 20 5.67158 20 8.50002V19.1458C20 20.6575 20 21.4133 19.538 21.7108C18.7831 22.1971 17.6161 21.1774 17.0291 20.8073C16.5441 20.5014 16.3017 20.3485 16.0325 20.3397C15.7417 20.3301 15.4949 20.4768 14.9709 20.8073L13.06 22.0124C12.5445 22.3374 12.2868 22.5 12 22.5C11.7132 22.5 11.4554 22.3374 10.94 22.0124Z"
                stroke="black"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <Typography
              variant="body2"
              sx={{
                color: "#1F2937",
                fontSize: { xs: "14px", md: "16px" },
                fontWeight: 500,
                fontFamily: "'Montserrat', sans-serif",
                lineHeight: "24px",
              }}
            >
              {job?.open_positions} Open positions
            </Typography>
          </Box>
        </Box>
      </CardContent>

      <Divider
        sx={{
          borderTop: "1px solid #E5E7EB",
          m: { xs: "0 20px 20px 20px", md: "0 32px" },
        }}
      />

      <CardActions
        sx={{
          p: { xs: 2, md: 3 },
          pt: 0,
          gap: { xs: 1, md: 1.5 },
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        <Button
          onClick={onApplyClick}
          variant="contained"
          sx={{
            flex: 1,
            width: { xs: "100%", sm: "auto" },
            backgroundColor: "#21404B",
            color: "white",
            fontWeight: 500,
            fontSize: { xs: "16px", md: "18px" },
            height: { xs: "48px", md: "56px" },
            p: { xs: "12px", md: "16px" },
            fontFamily: "'Montserrat', sans-serif",
            borderRadius: "1000px",
            textTransform: "none",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "#1F2937",
              boxShadow: "none",
            },
          }}
        >
          Apply now &nbsp;
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
              stroke="white"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M15 14V12C15 10.5858 15 9.87868 14.5607 9.43934C14.1213 9 13.4142 9 12 9H10M14 10L9 15"
              stroke="white"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Button>

        <Button
          onClick={onViewClick}
          variant="outlined"
          sx={{
            flex: 1,
            width: { xs: "100%", sm: "auto" },
            color: "#374151",
            borderColor: "#D1D5DB",
            fontWeight: 500,
            p: { xs: "12px", md: "16px" },
            fontFamily: "'Montserrat', sans-serif",
            borderRadius: "1000px",
            fontSize: { xs: "14px", md: "0.875rem" },
            height: { xs: "48px", md: "auto" },
            textTransform: "none",
            backgroundColor: "#21404B1A",
            "&:hover": {
              backgroundColor: "#F9FAFB",
              borderColor: "#9CA3AF",
              color: "#374151",
            },
          }}
        >
          View detail
        </Button>
      </CardActions>
    </Card>
  );
};

export default JobCard;
